import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';

import Loader from 'components/Loader';
import { getMVPDS, getAccountRegCode } from 'helpers/Account';
import { setCookie, removeCookie } from 'helpers/Storage';
import { previewPassLogout, SUCCESS_COOKIE_NAME } from 'helpers/AuthHelper';
import TopProviders from './components/TopProviders';
import ProviderSearchList from './components/ProviderSearchList';
import style from './MvpdProviders.module.scss';

const MvpdProviders = ({
  accessToken,
  deviceId,
  onSuccessClose,
  profile,
  mvpdLogin,
  selectedChannel,
  setAuthorizationToken,
  setMVPDToAppState,
}) => {
  const [mvpdList, setMvpdlist] = useState(null);
  const [regCode, setRegCode] = useState(null);
  const [filteredList, setFilteredList] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mvpdAuthError, setMVPDAuthError] = useState('');
  const [lastMVPDClicked, setLastClickedMVPD] = useState(null);

  const heading = 'Watch Fox News Channel';
  const subHeading = 'Link your TV Provider to watch Fox News Channel live within Fox Nation';
  const errorHeading = 'Something went wrong';
  const tryAgainButtonText = 'Try Again';
  const otherProviderText = 'Select Other TV Provider';

  async function getMvpdProviders() {
    const response = await getMVPDS(profile);
    const code = await getAccountRegCode({ profile, deviceId });
    const { member } = response;
    setMvpdlist(member);
    setRegCode(code);
    removeCookie({ key: SUCCESS_COOKIE_NAME });
  }

  useEffect(() => {
    if (!mvpdList || !regCode) {
      getMvpdProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTopProviders = mvpdList?.filter(provider => {
    if (provider.isFeatured || provider.numSubscribers) {
      return true;
    }
    return false;
  });

  const featuredInFront = (a, b) => {
    if (a.isFeatured) {
      return -1;
    }
    return 0;
  };

  const handleChange = e => {
    e.preventDefault();
    setSearchQuery(e.target.value);
    const filteredMvpds = mvpdList?.filter(
      mvpd => mvpd.name.toLowerCase().search(e.target.value.toLowerCase()) !== -1,
    );
    setFilteredList(filteredMvpds);
  };

  const handleMVPDSelect = async mvpdId => {
    const cookie = {
      name: SUCCESS_COOKIE_NAME,
      value: {
        selectedChannel,
        provider: mvpdId,
      },
      options: {
        path: '/',
      },
    };
    setCookie({ ...cookie });
    setIsLoading(true);
    setSearchQuery('');
    setIsFocused(false);
    setLastClickedMVPD({ mvpdId });

    const authProps = {
      accessToken,
      deviceId,
      setAuthorizationToken,
      setMVPDToAppState,
    };

    await previewPassLogout(authProps);

    const { success, account } = await mvpdLogin(mvpdId);

    if (success) {
      showAccountLinkToast(account);
      onSuccessClose();
    } else {
      setIsLoading(false);
      setMVPDAuthError(true);
    }
  };

  const showAccountLinkToast = authResult => {
    const linkedMVPD = authResult?.linkedMVPD;
    if (linkedMVPD.length > 0) {
      toast.success('Your TV Provider & Account are now linked!', {
        position: 'top-center',
        duration: 10000,
        iconTheme: {
          primary: '#fff',
          secondary: '#404040',
        },
        dismissible: false,
      });
    }
  };

  const retryMVPDButtonClick = () => {
    const { mvpdId } = lastMVPDClicked;
    setMVPDAuthError(null);
    handleMVPDSelect(mvpdId);
  };

  const topProviders = getTopProviders?.sort(featuredInFront).slice(0, 8);

  return (
    <div className={style.ProvidersContainer}>
      {!mvpdAuthError && (
        <>
          <h1 className={style.heading}>{heading}</h1>
          <span className={style.subHeading}>{subHeading}</span>
          <TopProviders
            handleMVPDSelect={handleMVPDSelect}
            topProviders={topProviders}
            profile={profile}
            regCode={regCode}
          />
          <ProviderSearchList
            filteredList={filteredList}
            handleChange={handleChange}
            handleMVPDSelect={handleMVPDSelect}
            isFocused={isFocused}
            mvpdList={mvpdList}
            searchQuery={searchQuery}
            setFilteredList={setFilteredList}
            setIsFocused={setIsFocused}
            mvpdAuthError={mvpdAuthError}
          />
        </>
      )}
      {(!mvpdList || isLoading) && (
        <div className={style.loaderContainer}>
          <Loader center />
        </div>
      )}
      {mvpdAuthError && !isLoading && (
        <div className={style.ProvidersContainer}>
          <h1 className={style.heading}>{errorHeading}</h1>
          <button className={style.retryBtn} onClick={retryMVPDButtonClick}>
            {tryAgainButtonText}
          </button>
          <button className={style.otherProviderBtn} onClick={() => setMVPDAuthError(null)}>
            {otherProviderText}
          </button>
        </div>
      )}
    </div>
  );
};

export default MvpdProviders;

MvpdProviders.propTypes = {
  accessToken: PropTypes.string,
  deviceId: PropTypes.string,
  mvpdLogin: PropTypes.func,
  onSuccessClose: PropTypes.func,
  profile: PropTypes.shape({}),
  selectedChannel: PropTypes.number,
  setAuthorizationToken: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
};
